/**
 * Custom Core routes
 * 
 * Extends the auto-generated core routes with custom routes
 * that are not part of the DSL (like Workflow Chaining)
 */

import { EntityRedirect } from '@/components/EntityRedirect';
import { lazy, Suspense } from 'react';
import { coreRoutes as generatedCoreRoutes } from './core.generated';
import { WorkflowChainingListView } from '@/core/workflows/WorkflowChainingListView';
import { WorkflowSubscriptionDetailView } from '@/core/workflows/WorkflowSubscriptionDetailView';
import { EventCatalogView, EventDetailView } from '@/core/workflows/EventCatalogView';
import { SearchPage } from '@/pages/search/SearchPage';
import { BusinessRulesListView } from '@/core/BusinessRulesListView';
import { SystemRuleDetailView } from '@/core/SystemRuleDetailView';
import { FieldRestrictionsManagementView } from '@/core/FieldRestrictionsManagementView';
import { OrgUnitTreeView } from '@/core/OrgUnitTreeView';
import { UserListPage } from '@/core/UserListPage';
import { RunAsUserPage } from '@/core/RunAsUserPage';
import { UserDetailPage } from '@/core/UserDetailPage';
import { RoleListPage } from '@/core/RoleListPage';
import { RoleDetailPage } from '@/core/RoleDetailPage';
import { UserSettingsPage } from '@/pages/UserSettingsPage';
import { TranslationManagementView } from '@/pages/core/TranslationManagementView';
import { AiAssistantSettingsPage } from '@/pages/core/AiAssistantSettingsPage';
import { ProcessMapPage } from '@/core/ProcessMapPage';
import { PolicyConfigurationDetailView } from '@/core/policies/PolicyConfigurationForm.generated';
import { DocumentTemplateEditorView } from '@/core/DocumentTemplateEditorView';

const GlobalDashboard = lazy(
  () => import('@/shared/dashboards/GlobalDashboard.generated').then(m => ({ default: m.GlobalDashboard }))
);

const DevToolsPage = lazy(
  () => import('@/pages/DevToolsPage').then(m => ({ default: m.DevToolsPage }))
);

/**
 * Custom core routes (workflow chaining)
 */
const customCoreRoutes = [
  {
    path: '/shared/dashboard',
    element: (
      <Suspense fallback={null}>
        <GlobalDashboard />
      </Suspense>
    ),
  },
  {
    path: '/search',
    element: <SearchPage />
  },
  {
    path: '/core/businessrules',
    element: <BusinessRulesListView />
  },
  {
    path: '/core/businessrules/system/:id',
    element: <SystemRuleDetailView />
  },
  {
    path: '/core/organization',
    element: <OrgUnitTreeView />
  },
  {
    path: '/core/users',
    element: <UserListPage />
  },
  {
    path: '/core/users/new',
    element: <UserDetailPage />
  },
  {
    path: '/core/users/:id',
    element: <UserDetailPage />
  },
  {
    path: '/core/run-as',
    element: <RunAsUserPage />
  },
  {
    path: '/core/roles',
    element: <RoleListPage />
  },
  {
    path: '/core/roles/new',
    element: <RoleDetailPage />
  },
  {
    path: '/core/roles/:id',
    element: <RoleDetailPage />
  },
  {
    path: '/core/policies/new',
    element: <PolicyConfigurationDetailView />
  },
  {
    path: '/core/policies/:id',
    element: <PolicyConfigurationDetailView />
  },
  {
    path: '/core/fieldrestrictionsets',
    element: <FieldRestrictionsManagementView />
  },
  {
    path: '/core/fieldrestrictionsets/:id',
    element: <FieldRestrictionsManagementView />
  },
  {
    // Overrides the generated detail route: document templates open in the
    // dedicated editor (source + placeholder palette + live PDF preview)
    // instead of the generated CRUD form. The list view stays generated.
    path: '/core/documenttemplates/:id',
    element: <DocumentTemplateEditorView />
  },
  {
    path: '/core/workflow-chaining',
    element: <WorkflowChainingListView />
  },
  {
    path: '/core/workflow-chaining/subscriptions/new',
    element: <WorkflowSubscriptionDetailView />
  },
  {
    path: '/core/workflow-chaining/subscriptions/:id',
    element: <WorkflowSubscriptionDetailView />
  },
  {
    path: '/core/workflow-chaining/events',
    element: <EventCatalogView />
  },
  {
    path: '/core/workflow-chaining/events/:eventType',
    element: <EventDetailView />
  },
  {
    path: '/core/process-map',
    element: <ProcessMapPage />
  },
  {
    path: '/core/translations',
    element: <TranslationManagementView />
  },
  {
    path: '/core/ai-assistant-settings',
    element: <AiAssistantSettingsPage />
  },
  {
    path: '/user/settings',
    element: <UserSettingsPage />
  },
  {
    path: '/dev',
    element: (
      <Suspense fallback={null}>
        <DevToolsPage />
      </Suspense>
    ),
  }
];

/**
 * App-wide utility routes that belong to no single module.
 *
 * `/go/:entityType/:entityId` is an entity-agnostic deep link. Server-side producers
 * (notification emails, integrations) need one stable URL shape for "this record", but the
 * entity→route map is generated frontend config — mirroring it server-side would leave two
 * maps to drift apart. Lives here rather than in the route aggregator so the aggregator stays
 * a plain .ts list with no JSX.
 */
const utilityRoutes = [
  { path: '/go/:entityType/:entityId', element: <EntityRedirect /> },
];

/**
 * Combined core routes (custom routes first to override generated)
 */
export const coreRoutes = [
  ...utilityRoutes,
  ...customCoreRoutes,
  ...generatedCoreRoutes
];

